// src/pages/MyMood.tsx
import React, { useState, useEffect } from 'react';
import DonationSidebar from '../components/DonationSidebar';

const MyMood: React.FC = () => {
  const [selectedMood, setSelectedMood] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const moods = [
    { name: 'Angry', color: '#DC2626' },
    { name: 'Anxious', color: '#EA580C' },
    { name: 'Depressed', color: '#475569' },
    { name: 'Confused', color: '#9333EA' },
    { name: 'Scared', color: '#4338CA' },
    { name: 'Overwhelmed', color: '#BE185D' },
    { name: 'Grateful', color: '#059669' },
    { name: 'Guilty', color: '#B45309' },
    { name: 'Lonely', color: '#1D4ED8' },
    { name: 'Uncertain', color: '#6B7280' }
  ];

  const moodGuidance: { [key: string]: { 
    title: string; 
    content: string; 
    duas: { 
      arabic: string; 
      transliteration: string; 
      translation: string; 
      reference: string;
    }[] 
  }} = {
    Angry: {
      title: "Guidance for When You Feel Angry",
      content: "The Prophet Muhammad (PBUH) said: 'If any of you becomes angry, let him keep silent.' (Ahmad) and 'The strong is not the one who overcomes the people by his strength, but the strong is the one who controls himself while in anger.' (Bukhari)",
      duas: [
        {
          arabic: "أَعُوذُ بِاللَّهِ مِنَ الشَّيْطَانِ الرَّجِيمِ",
          transliteration: "A'ūdhu billāhi mina ash-shayṭāni ar-rajīm",
          translation: "I seek refuge in Allah from Satan, the accursed.",
          reference: "Qur'an 16:98; Sahih al-Bukhari 3282; Sahih Muslim 2610"
        }
      ]
    },
    Anxious: {
      title: "Finding Peace in Anxious Times",
      content: "Remember that Allah is with you. The Prophet (PBUH) said: 'If anyone continually asks pardon, Allah will appoint for him a way out of every distress, and a relief from every anxiety, and will provide for him from where he did not expect.' (Abu Dawud)",
      duas: [
        {
          arabic: "اللَّهُمَّ إِنِّي أَعُوذُ بِكَ مِنَ الْهَمِّ وَالْحُزْنِ",
          transliteration: "Allahumma innī a'ūdhu bika minal-hammi wal-ḥuzn",
          translation: "O Allah! I seek refuge in You from anxiety and sorrow.",
          reference: "Sahih al-Bukhari 6369; Sahih Muslim 2706"
        }
      ]
    },
    Depressed: {
      title: "Finding Hope in Darkness",
      content: "Know that Allah tests those He loves. The Prophet Muhammad (PBUH) said: 'No fatigue, nor disease, nor sorrow, nor sadness, nor hurt, nor distress befalls a Muslim, even if it were the prick he receives from a thorn, but that Allah expiates some of his sins for that.' (Bukhari)",
      duas: [
        {
          arabic: "لَا إِلَهَ إِلَّا أَنْتَ سُبْحَانَكَ إِنِّي كُنتُ مِنَ الظَّالِمِينَ",
          transliteration: "Lā ilāha illā anta subḥānaka innī kuntu minaz-zālimīn",
          translation: "There is no god but You; glory be to You! I was indeed among the wrongdoers.",
          reference: "Qur'an 21:87 (Dua of Prophet Yunus عليه السلام)"
        }
      ]
    },
    Confused: {
      title: "Seeking Clarity and Direction",
      content: "When confused or lost, turn to Allah for clarity and ease in your affairs. Remember that Allah guides those who seek His guidance.",
      duas: [
        {
          arabic: "رَبِّ اشْرَحْ لِي صَدْرِي وَيَسِّرْ لِي أَمْرِي",
          transliteration: "Rabbi shraḥ lī ṣadrī wa yassir lī amrī",
          translation: "My Lord, expand my chest and make my task easy for me.",
          reference: "Qur'an 20:25–26 (Prophet Musa عليه السلام)"
        }
      ]
    },
    Scared: {
      title: "Protection from Fear",
      content: "Seek refuge in Allah from all fears and difficulties. The Prophet (PBUH) said: 'Whoever says in the morning and evening: 'I am pleased with Allah as my Lord, with Islam as my religion, and with Muhammad as my Prophet,' Allah will be pleased with him on the Day of Resurrection.' (Ahmad)",
      duas: [
        {
          arabic: "اللَّهُمَّ إِنِّي أَعُوذُ بِكَ مِنْ جَهْدِ الْبَلَاءِ وَدَرَكِ الشَّقَاءِ",
          transliteration: "Allahumma innī a'ūdhu bika min jahdil-balā'i wa darakish-shaqā'i",
          translation: "O Allah! I seek refuge in You from the hardship of trial and the reach of misery.",
          reference: "Sahih al-Bukhari 6347; Sahih Muslim 2707"
        }
      ]
    },
    Overwhelmed: {
      title: "Finding Strength in Weakness",
      content: "Remember that with hardship comes ease. When you feel overwhelmed, turn to Allah for strength and support.",
      duas: [
        {
          arabic: "اللَّهُمَّ لَا تَكِلْنِي إِلَى نَفْسِي طَرْفَةَ عَيْنٍ",
          transliteration: "Allahumma lā takilnī ilā nafsī ṭarfata 'aynin",
          translation: "O Allah! Do not leave me to myself even for the blink of an eye.",
          reference: "Abu Dawood 5090 (Hasan Hadith)"
        }
      ]
    },
    Grateful: {
      title: "Expressing Gratitude and Contentment",
      content: "Continue to be grateful to Allah for His blessings. The Prophet (PBUH) said: 'Whoever does not thank people has not thanked Allah.' (Abu Dawud)",
      duas: [
        {
          arabic: "الْحَمْدُ لِلَّهِ عَلَى كُلِّ حَالٍ",
          transliteration: "Al-ḥamdu lillāhi 'alā kulli ḥāl",
          translation: "Praise be to Allah in every condition.",
          reference: "Sunan Ibn Majah 3803"
        }
      ]
    },
    Guilty: {
      title: "Seeking Forgiveness and Renewal",
      content: "Allah's mercy encompasses all things. Turn to Him in sincere repentance. The Prophet (PBUH) said: 'All the children of Adam constantly err, but the best of those who constantly err are those who constantly repent.' (Tirmidhi)",
      duas: [
        {
          arabic: "رَبَّنَا ظَلَمْنَا أَنْفُسَنَا وَإِن لَّمْ تَغْفِرْ لَنَا وَتَرْحَمْنَا لَنَكُونَنَّ مِنَ الْخَاسِرِينَ",
          transliteration: "Rabbana ẓalamna anfusanā wa in lam taghfir lanā wa tarḥamnā lanakūnanna minal-khāsirīn",
          translation: "Our Lord! We have wronged ourselves, and if You do not forgive us and have mercy upon us, we will surely be among the losers.",
          reference: "Qur'an 7:23 (Adam عليه السلام & Hawwa)"
        }
      ]
    },
    Lonely: {
      title: "Comfort in Solitude",
      content: "Remember that Allah is always with you, even in your loneliest moments. The Prophet (PBUH) said: 'Allah says: I am as My servant thinks I am. I am with him when he makes mention of Me.' (Bukhari)",
      duas: [
        {
          arabic: "يَا حَيُّ يَا قَيُّومُ بِرَحْمَتِكَ نَسْتَغِيثُ",
          transliteration: "Yā Ḥayyū Yā Qayyūmu bir-raḥmatika nastaghīth",
          translation: "O Ever-Living, O Sustainer! By Your mercy, we seek help.",
          reference: "Jami' al-Tirmidhi 3524"
        }
      ]
    },
    Uncertain: {
      title: "Seeking Certainty and Guidance",
      content: "When in doubt or uncertainty, seek wisdom and clear direction from Allah. Trust in His plan and timing.",
      duas: [
        {
          arabic: "اللَّهُمَّ إِنِّي أَسْأَلُكَ هُدًى وَتُقًى وَعَفَافًا وَغِنًى",
          transliteration: "Allahumma innī as'aluka hudā wa tuqā wa 'afāfa wa ghinā",
          translation: "O Allah! I ask You for guidance, piety, chastity, and sufficiency.",
          reference: "Sahih Muslim 2725"
        }
      ]
    }
  };

  const getMoodGuidance = (mood: string) => {
    return moodGuidance[mood] || moodGuidance.Anxious; // Fallback to Anxious if mood not found
  };

  const handleMoodSelect = (mood: string) => {
    setSelectedMood(mood);
    window.scrollTo(0, 0);
  };

  return (
    <div className="section-padding">
      <div className="container">
        <div className="section-title">
          <h1>How Are You Feeling Today?</h1>
          <p>Select your current emotional state to receive Islamic guidance and comfort</p>
        </div>

        {/* Mood Selection Grid */}
        <div style={{ marginBottom: '48px' }}>
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: '600', 
            marginBottom: '24px', 
            textAlign: 'center',
            color: 'var(--text-primary)'
          }}>
            I feel...
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: '16px',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {moods.map((mood, index) => (
              <button
                key={index}
                onClick={() => handleMoodSelect(mood.name)}
                style={{
                  padding: '20px 12px',
                  borderRadius: '12px',
                  border: `2px solid ${selectedMood === mood.name ? mood.color : 'var(--border)'}`,
                  backgroundColor: selectedMood === mood.name ? `${mood.color}20` : 'var(--card-bg)',
                  color: 'var(--text-primary)',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: '16px',
                  minHeight: '70px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center'
                }}
              >
                {mood.name}
              </button>
            ))}
          </div>
        </div>

        {/* Guidance Section */}
        {selectedMood && (
          <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '600', 
              marginBottom: '20px',
              color: 'var(--text-primary)',
              textAlign: 'center'
            }}>
              {getMoodGuidance(selectedMood).title}
            </h2>
            
            <div style={{ 
              color: 'var(--text-secondary)', 
              lineHeight: '1.7',
              marginBottom: '24px',
              fontSize: '16px',
              textAlign: 'center'
            }}>
              {getMoodGuidance(selectedMood).content}
            </div>

            {/* Duas Section */}
            <div>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                marginBottom: '20px',
                color: 'var(--text-primary)',
                textAlign: 'center',
                borderBottom: '2px solid var(--accent-primary)',
                paddingBottom: '8px'
              }}>
                Recommended Dua
              </h3>
              
              {getMoodGuidance(selectedMood).duas.map((dua, index) => (
                <div key={index} style={{
                  backgroundColor: 'var(--bg-secondary)',
                  padding: '24px',
                  borderRadius: '12px',
                  marginBottom: '20px',
                  border: '1px solid var(--border)'
                }}>
                  {/* Arabic Text */}
                  <div className="arabic" style={{
                    fontSize: '24px',
                    marginBottom: '20px',
                    textAlign: 'center',
                    lineHeight: '1.8',
                    direction: 'rtl',
                    fontFamily: "'Amiri', serif"
                  }}>
                    {dua.arabic}
                  </div>
                  
                  {/* Transliteration */}
                  <div style={{
                    color: 'var(--accent-primary)',
                    fontSize: '16px',
                    fontStyle: 'italic',
                    marginBottom: '16px',
                    textAlign: 'center',
                    lineHeight: '1.6'
                  }}>
                    {dua.transliteration}
                  </div>
                  
                  {/* Translation */}
                  <div style={{
                    color: 'var(--text-secondary)',
                    fontSize: '16px',
                    marginBottom: '16px',
                    textAlign: 'center',
                    lineHeight: '1.6',
                    fontStyle: 'italic',
                    borderLeft: '3px solid var(--accent-primary)',
                    paddingLeft: '16px',
                    marginLeft: '20px',
                    marginRight: '20px'
                  }}>
                    "{dua.translation}"
                  </div>
                  
                  {/* Reference */}
                  <p style={{ 
                    color: 'var(--text-secondary)', 
                    fontSize: '14px',
                    textAlign: 'center',
                    margin: 0,
                    opacity: '0.8',
                    borderTop: '1px solid var(--border)',
                    paddingTop: '16px'
                  }}>
                    <strong>Reference:</strong> {dua.reference}
                  </p>
                </div>
              ))}
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginTop: '24px'
            }}>
              <button 
                className="btn-primary"
                onClick={() => setSelectedMood(null)}
              >
                Select Different Mood
              </button>
              <button 
                className="btn-secondary"
                onClick={() => window.scrollTo(0, 0)}
              >
                Back to Top
              </button>
            </div>
          </div>
        )}

        {/* Donation Sidebar */}
        <DonationSidebar />
      </div>
    </div>
  );
};

export default MyMood;